import type { ApiResource } from '../schema';

export const resource: ApiResource = {
  id: 'billing-ops',
  slug: 'billing-ops',
  name: 'Billing Operations',
  description: 'Recurring billing accounts, AR aging and the account ledger, credit notes, dunning drafts, refunds, and quote-to-invoice conversion — the operational finance layer sitting behind an invoice: the accounts that get charged on a schedule, the ledger entries that explain a balance, and the follow-up workflows around past-due money.',
  icon: 'AccountBalance',
  endpoints: [
    {
      slug: 'billing-accounts',
      method: 'GET',
      path: '/api/billing/accounts',
      summary: 'List recurring billing accounts',
      description: 'Lists the tenant\'s recurring-billing accounts — the record that maps a client/contract to a payment-processor customer for scheduled auto-charging. A new account is created with POST /api/billing/accounts. The app only ever configures and authorizes recurring billing here — the processor itself executes the charge on its own schedule; there is no direct charge/capture call in this API.',
      auth: 'session',
      responseExample: `[
  {
    "id": "acct_2b7e",
    "contractId": "ctr_9910",
    "client": "St. Edward Academy",
    "email": "ap@stedwardacademy.example",
    "hasMethod": true,
    "pmType": "us_bank_account",
    "pmLast4": "6789",
    "authorized": true,
    "authorizedAt": "2026-05-01T10:00:00.000Z",
    "subscriptionId": "sub_sim_2210",
    "subscriptionStatus": "active",
    "amount": 1450,
    "cadence": "monthly",
    "lastInvoiceAt": "2026-07-01T06:00:00.000Z",
    "createdAt": "2026-04-28T09:00:00.000Z"
  }
]`,
      sourceFile: 'routes/billing-recurring.js',
    },
    {
      slug: 'subscribe-billing-account',
      method: 'POST',
      path: '/api/billing/accounts/:id/subscribe',
      summary: 'Attach a recurring subscription to a billing account',
      description: 'Maps a billing account\'s linked contract to a recurring subscription with the connected payment processor, so future invoices auto-charge on the given cadence. Requires the account to already be marked authorized and, if the processor is configured, to have a saved payment method — both checks fail with a 400 and an explanatory message. amount/cadence fall back to the linked contract\'s values when not supplied.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Billing account id.' }],
      requestBodyExample: `{ "amount": 1450, "cadence": "monthly" }`,
      responseExample: `{
  "account": { "id": "acct_2b7e", "client": "St. Edward Academy", "subscriptionId": "sub_sim_2210", "subscriptionStatus": "active", "amount": 1450, "cadence": "monthly" },
  "simulated": false
}`,
      sourceFile: 'routes/billing-recurring.js',
    },
    {
      slug: 'ar-aging',
      method: 'GET',
      path: '/api/billing/aging',
      summary: 'Get accounts-receivable aging report',
      description: 'Returns an AR aging report grouped by customer, bucketing every unpaid, non-draft, non-void invoice\'s outstanding balance by days overdue (current, 1-30, 31-60, 61-90, 90+ relative to ?asOf, default today). Balance already accounts for recorded payments and applied credit notes, so a partially-paid or partially-credited invoice ages only on what actually remains.',
      auth: 'session',
      params: [{ name: 'asOf', in: 'query', type: 'string', required: false, description: 'ISO yyyy-mm-dd date to age against (default: today).' }],
      responseExample: `{
  "asOf": "2026-07-21",
  "customers": [
    {
      "customer": "Holy Rosary Academy",
      "buckets": { "current": 0, "1-30": 1450.00, "31-60": 0, "61-90": 0, "90+": 0 },
      "total": 1450.00,
      "count": 1
    }
  ],
  "grandTotal": 1450.00
}`,
      sourceFile: 'routes/billingextras.js, lib/billingextras.js',
    },
    {
      slug: 'credit-notes',
      method: 'GET',
      path: '/api/credit-notes',
      summary: 'List credit notes',
      description: 'Lists credit notes — non-cash reductions against a customer invoice for a billing error, service credit, or goodwill adjustment. Optional ?invoiceId= filter. Credit notes are drafts-first: created via POST /api/credit-notes against an existing invoice (amount capped at the invoice\'s remaining balance), then explicitly moved through POST /api/credit-notes/:id/issue and POST /api/credit-notes/:id/apply, or POST /api/credit-notes/:id/void before it\'s applied. This never moves money or touches a payment processor — applying a credit note only reduces the invoice\'s effective balance.',
      auth: 'session',
      params: [{ name: 'invoiceId', in: 'query', type: 'string', required: false, description: 'Filter to credit notes against one invoice.' }],
      responseExample: `[
  {
    "id": "cn_5510",
    "number": "CN-1014",
    "invoiceId": "inv_6602",
    "invoiceNumber": "INV-1088",
    "billToName": "Holy Rosary Academy",
    "amount": 150.00,
    "reason": "Double-billed onsite visit on 2026-06-18",
    "status": "issued",
    "createdAt": "2026-07-19T14:00:00.000Z"
  }
]`,
      sourceFile: 'routes/credit-notes.js',
    },
    {
      slug: 'dunning-overdue',
      method: 'GET',
      path: '/api/billing/dunning',
      summary: 'List overdue invoices with suggested reminder level',
      description: 'Returns every overdue invoice as of ?asOf (default today), with days overdue, aging bucket, and a suggested reminder escalation level (friendly / firm / final-notice) based on how overdue it is. Draft reminder text is generated with POST /api/billing/dunning/draft. This module is drafts-only by hard invariant: it never charges, captures, or auto-emails anything — drafting only stamps a lastDunningDraftAt timestamp on the invoice so the UI shows a reminder was prepared.',
      auth: 'session',
      params: [{ name: 'asOf', in: 'query', type: 'string', required: false, description: 'ISO yyyy-mm-dd date to age against (default: today).' }],
      responseExample: `{
  "asOf": "2026-07-21",
  "count": 1,
  "overdue": [
    { "id": "inv_6602", "number": "INV-1088", "billToName": "Holy Rosary Academy", "dueDate": "2026-07-05", "daysOverdue": 16, "bucket": "1-30", "balance": 1450.00, "level": "friendly" }
  ]
}`,
      sourceFile: 'routes/dunning.js',
    },
    {
      slug: 'refund-invoice',
      method: 'POST',
      path: '/api/invoices/:id/refund',
      summary: 'Issue a refund against an invoice',
      description: 'Reverses a real payment-processor charge on a paid invoice — the one deliberate exception to this app\'s "billing never moves money" posture. Hard-capped at whatever the processor confirmed as paid on this invoice minus any amount already refunded through it, so a manually-recorded or non-processor payment can never be refunded through a live payment-reversal call; 400s if the invoice has no confirmed processor payment. Refund attempts are idempotency-keyed and persisted as a durable intent before the provider call, so a retry after a timeout/crash safely reuses the same operation instead of double-refunding.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Invoice id.' }],
      requestBodyExample: `{ "amount": 500.00, "reason": "requested_by_customer" }`,
      responseExample: `{
  "invoice": { "id": "inv_6602", "number": "INV-1088", "status": "sent", "paidAmount": 950.00 },
  "refund": { "id": "re_sim_7712", "status": "succeeded" },
  "simulated": false
}`,
      sourceFile: 'routes/refunds.js',
    },
    {
      slug: 'billing-ledger',
      method: 'GET',
      path: '/api/billing/ledger',
      summary: 'Get ledger entries behind invoices and payments',
      description: 'Returns the raw charge/payment/credit ledger entries that explain a billing account\'s balance — the audit trail underneath every invoice and payment record. Optional ?accountId= filter; entries are returned oldest first. New entries are recorded with POST /api/billing/ledger. This module makes no outbound calls and moves no money itself — it only records what already happened elsewhere and reports on it.',
      auth: 'session',
      params: [{ name: 'accountId', in: 'query', type: 'string', required: false, description: 'Filter to ledger entries for one billing account.' }],
      responseExample: `[
  { "id": "ledg_3391", "accountId": "acct_2b7e", "type": "charge", "amount": 1450.00, "date": "2026-07-01", "desc": "July managed services", "ref": "INV-1091" },
  { "id": "ledg_3402", "accountId": "acct_2b7e", "type": "payment", "amount": 1450.00, "date": "2026-07-03", "desc": "ACH auto-charge", "ref": "sub_sim_2210" }
]`,
      sourceFile: 'routes/billing-ledger.js, lib/billing-ledger.js',
    },
    {
      slug: 'quote-product-search',
      method: 'GET',
      path: '/api/quote-products/search',
      summary: 'Search vendor product catalogs for a quote',
      description: 'Live product search against a configured vendor catalog (?vendor=, default "bestbuy"; ?q= is the search term) for building out quote line items — pricing, SKU, and product name pulled from the live vendor API. Rate-limited per tenant+user since this consumes a third-party API budget. Open to any authenticated user, same visibility as reading quotes.',
      auth: 'session',
      params: [
        { name: 'vendor', in: 'query', type: 'string', required: false, description: 'Product-search vendor key (default "bestbuy").' },
        { name: 'q', in: 'query', type: 'string', required: true, description: 'Search term.' },
      ],
      responseExample: `{
  "ok": true,
  "provider": "bestbuy",
  "results": [
    { "sku": "6418599", "name": "Lenovo 300e Gen 4 2-in-1 Chromebook", "price": 349.99, "url": "https://www.bestbuy.com/site/6418599.p" }
  ]
}`,
      sourceFile: 'routes/quote-products.js, lib/quote-catalog.js, lib/vendor/bestbuy.js',
    },
    {
      slug: 'convert-quote-to-invoice',
      method: 'POST',
      path: '/api/quotes/:id/convert-to-invoice',
      summary: 'Convert an accepted quote into an invoice',
      description: 'Creates a draft invoice from a quote — never sent or charged automatically. If the quote has structured lineItems the invoice is itemized to match; otherwise it falls back to one lump-sum line. 400s if the quote is already linked to an invoice or has no amount. billToName is required in the body. On success, the quote is stamped invoiceStatus:"invoiced" with the new invoice number.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Quote id.' }],
      requestBodyExample: `{ "billToName": "St. Edward Academy", "billToEmail": "ap@stedwardacademy.example" }`,
      responseExample: `{
  "quote": { "id": "qte_2210", "title": "Fall refresh — 20x Chromebooks", "invoiceNumber": "INV-1092", "invoiceStatus": "invoiced" },
  "invoice": {
    "id": "inv_7721",
    "number": "INV-1092",
    "billToName": "St. Edward Academy",
    "status": "draft",
    "items": [{ "desc": "Fall refresh — 20x Chromebooks", "qty": 20, "unitPrice": 349.99, "amount": 6999.80 }],
    "total": 6999.80,
    "quoteId": "qte_2210"
  }
}`,
      sourceFile: 'routes/quotes-convert.js',
    },
  ],
};
